import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, X } from 'lucide-react';
import Button from '@/components/common/Button/Button';
import Modal from '@/components/common/Modal/Modal';
import { getSocket, encode, decode, C } from '@/utils';
import { useToast } from '@/hooks/useToast';
import {
  useUserBonus,
  useBonuses,
  useBonusRedeem,
  useBonusHistory,
  useClaimBonus,
  useRedeemBonusCode,
} from '@/hooks/useBonus';
import BonusDetails from './BonusDetails';
import styles from './BonusLoggedInView.module.css';

const BONUS_INFO = {
  dailyBonus: {
    title: 'Daily Bonus',
    points: [
      'Login daily to claim your bonus rewards',
      'Available for VIP level 25 and above',
      'Bonus amount increases with VIP level',
      'Must be claimed within 24 hours',
      'Wagering requirements may apply',
    ],
  },
  rakeback: {
    title: 'Rakeback Bonus',
    points: [
      'Earn back a percentage of your house edge',
      'No minimum bet requirements',
      'Available for all casino games',
      'Instant crediting to your account',
      'Higher percentages for VIP members',
    ],
  },
  weeklyCashback: {
    title: 'Weekly Cashback',
    points: [
      'The Weekly Cashback is a personalized bonus based on your recent activity',
      'We aim to drop Weekly Cashback every Friday.',
    ],
  },
  monthlyCashback: {
    title: 'Monthly Cashback',
    points: [
      'The Monthly Cashback is a personalized bonus based on your recent activity.',
      'We aim to drop Monthly Cashback every 15th of the month.',
    ],
  },
};

const BonusLoggedInView = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('1');
  const [rakebackAmount, setRakebackAmount] = useState(0);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // React Query hooks
  const { data: userBonus, isLoading: isLoadingUserBonus } = useUserBonus();
  const { data: bonuses, isLoading: isLoadingBonuses } = useBonuses();
  const { data: bonusRedeem, isLoading: isLoadingRedeem } = useBonusRedeem();
  const { data: bonusHistory, isLoading: isLoadingHistory } = useBonusHistory();
  const claimBonusMutation = useClaimBonus();
  const redeemCodeMutation = useRedeemBonusCode();

  const tabs = [
    { id: '1', label: 'General bonus' },
    { id: '2', label: 'VIP Bonus' },
  ];

  // Socket integration for rakeback
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const initializeSocket = async () => {
      await new Promise((resolve) => setTimeout(resolve, 200));
      socket.emit(C.RAKEBACK_AMOUNT);
    };

    initializeSocket();

    const handleRakebackAmount = (data) => {
      try {
        const decodedData = decode(data);
        if (decodedData && decodedData.amount !== undefined) {
          setRakebackAmount(decodedData.amount || 0);
        }
      } catch (error) {
        console.error('Error decoding rakeback amount:', error);
      }
    };

    const handleAddRakeback = async (data) => {
      try {
        const decodedData = decode(data);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        socket.emit(C.CREDIT);
        showSuccess(decodedData.status?.toString() || 'Rakeback claimed successfully');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Error processing rakeback:', error);
        showError('Failed to claim rakeback');
      }
    };

    socket.on(C.RAKEBACK_AMOUNT, handleRakebackAmount);
    socket.on(C.ADD_RAKEBACK, handleAddRakeback);

    return () => {
      socket.off(C.RAKEBACK_AMOUNT, handleRakebackAmount);
      socket.off(C.ADD_RAKEBACK, handleAddRakeback);
    };
  }, [showSuccess, showError]);

  const handleClaimRakeback = () => {
    const socket = getSocket();
    if (socket) {
      socket.emit(C.ADD_RAKEBACK);
      // Play sound
      const audio = new Audio('/sounds/magic-spell.mp3');
      audio.play().catch((error) => console.error('Error playing audio:', error));
    }
  };

  const handleClaimBonus = (bonusType) => {
    claimBonusMutation.mutate(bonusType);
  };

  const handleRedeemCode = () => {
    if (!redeemCode.trim()) {
      showError('Please enter a redeem code');
      return;
    }
    redeemCodeMutation.mutate(redeemCode.trim());
    setRedeemCode('');
  };

  const calculateTotalBonus = () => {
    if (!userBonus) return 0;
    return (
      (userBonus.specialbonus || 0) +
      (userBonus.joiningbonus || 0) +
      (userBonus.actualdailybonus || 0) +
      (userBonus.actualweeklybonus || 0) +
      (userBonus.actualmonthlybonus || 0) +
      (userBonus.rakebonus || 0)
    );
  };

  const totalBonus = calculateTotalBonus();
  const isLoading =
    isLoadingUserBonus || isLoadingBonuses || isLoadingRedeem || isLoadingHistory;

  return (
    <>
      {/* Info Modal */}
      <Modal
        isOpen={selectedInfo !== null}
        onClose={() => setSelectedInfo(null)}
        size="md"
        title={selectedInfo?.title || 'Bonus Information'}
      >
        <ul className={styles.infoList}>
          {selectedInfo?.points.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </Modal>

      {/* Redeem Code Modal */}
      <Modal
        isOpen={showRedeemModal}
        onClose={() => {
          setShowRedeemModal(false);
          setRedeemCode('');
        }}
        size="sm"
        title="Redeem Code"
      >
        <div className={styles.redeemModalContent}>
          <label htmlFor="redeemCode" className={styles.redeemLabel}>
            Enter your code
          </label>
          <input
            type="text"
            id="redeemCode"
            className={styles.redeemInput}
            placeholder="Enter code"
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRedeemCode();
              }
            }}
          />
          <Button
            variant="primary"
            size="lg"
            onClick={handleRedeemCode}
            disabled={redeemCodeMutation.isPending}
            className={styles.redeemButton}
          >
            {redeemCodeMutation.isPending ? 'Redeeming...' : 'Redeem'}
          </Button>
        </div>
      </Modal>

      {/* Bonus Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        size="md"
        title="Bonus Details"
      >
        <BonusDetails data={userBonus} />
      </Modal>

      <div className={styles.bonusContainer}>
        {/* Header with Redeem Code (Desktop) */}
        <div className={styles.headerSection}>
          <h1 className={styles.pageTitle}>Bonus</h1>
          <div className={styles.redeemWrapper}>
            <input
              type="text"
              className={styles.redeemInput}
              placeholder="Redeem your bonus here."
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRedeemCode();
                }
              }}
            />
            <Button
              variant="primary"
              size="md"
              onClick={handleRedeemCode}
              disabled={redeemCodeMutation.isPending}
              className={styles.redeemButtonHeader}
            >
              {redeemCodeMutation.isPending ? 'Redeeming...' : 'Redeem Code'}
            </Button>
          </div>
        </div>

        {/* Banner Section */}
        <div className={styles.bannerCard}>
          <div className={styles.bannerImageBackground}></div>
          <div className={styles.bannerOverlay}></div>
          <div className={styles.bannerContent}>
            <div className={styles.bannerHeader}>
              <div className={styles.bannerLabel}>Total Bonus Claimed(USD)</div>
            </div>
            <div className={styles.bannerAmount}>
              ${totalBonus.toFixed(2)}
            </div>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowRedeemModal(true)}
              className={styles.redeemCodeButton}
            >
              Redeem Code
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabContainer}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loader}>Loading...</div>
          </div>
        ) : (
          <div className={styles.contentArea}>
            {activeTab === '1' && (
              <div className={styles.bonusGrid}>
                {/* Daily Bonus */}
                <div className={styles.bonusCardWrapper}>
                  <button
                    className={styles.infoIcon}
                    onClick={() => setSelectedInfo(BONUS_INFO.dailyBonus)}
                    aria-label="Daily Bonus Info"
                  >
                    <Info size={20} />
                  </button>
                  <div
                    className={styles.bonusCard}
                    style={{
                      background:
                        'radial-gradient(71.39% 51.03% at 46.49% 7.5%, rgba(111, 60, 255, .6) 0%, rgba(111, 60, 255, 0) 98%)',
                    }}
                  >
                    <div className={styles.bonusIconWrapper}>
                      <img
                        src="/assets/images/bonus/bcc1.webp"
                        alt="Daily Bonus"
                        className={styles.bonusIcon}
                        onError={(e) => {
                          e.target.src = '/assets/images/bonus/daily.png';
                        }}
                      />
                    </div>
                    <div className={styles.bonusCardContent}>
                      <h3 className={styles.bonusCardTitle}>Daily Bonus</h3>
                      <div className={styles.bonusCardStats}>
                        <span>
                          {bonuses?.bonuses?.daily?.eligible
                            ? 'Bonus Unlocked'
                            : 'Available at VIP 20'}
                        </span>
                        {bonuses?.bonuses?.daily?.eligible &&
                          bonuses?.bonuses?.daily?.claimable && (
                            <span>${bonuses.bonuses.daily.amount?.toFixed(2)}</span>
                          )}
                      </div>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => handleClaimBonus('daily')}
                        disabled={
                          !bonuses?.bonuses?.daily?.eligible ||
                          !bonuses?.bonuses?.daily?.claimable ||
                          claimBonusMutation.isPending
                        }
                        className={styles.claimButton}
                      >
                        {claimBonusMutation.isPending ? 'Loading...' : 'Claim'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Rakeback */}
                <div className={styles.bonusCardWrapper}>
                  <button
                    className={styles.infoIcon}
                    onClick={() => setSelectedInfo(BONUS_INFO.rakeback)}
                    aria-label="Rakeback Info"
                  >
                    <Info size={20} />
                  </button>
                  <div
                    className={styles.bonusCard}
                    style={{
                      background:
                        'radial-gradient(116.81% 83.5% at 46.23% 9.2%, rgba(214, 129, 0, .6) 0%, rgba(214, 129, 0, 0) 60%)',
                    }}
                  >
                    <div className={styles.bonusIconWrapper}>
                      <img
                        src="/assets/images/bonus/bcc2.webp"
                        alt="Rakeback"
                        className={styles.bonusIcon}
                        onError={(e) => {
                          e.target.src = '/assets/images/bonus/rakeback.png';
                        }}
                      />
                    </div>
                    <div className={styles.bonusCardContent}>
                      <h3 className={styles.bonusCardTitle}>Rakeback</h3>
                      <div className={styles.bonusCardStats}>
                        <span>Ready to claim:</span>
                        <span>${rakebackAmount.toFixed(2)}</span>
                      </div>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleClaimRakeback}
                        disabled={rakebackAmount <= 0}
                        className={styles.claimButton}
                      >
                        Claim
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === '2' && (
              <div className={styles.bonusGrid}>
                {/* Weekly Cashback */}
                <div className={styles.bonusCardWrapper}>
                  <button
                    className={styles.infoIcon}
                    onClick={() => setSelectedInfo(BONUS_INFO.weeklyCashback)}
                    aria-label="Weekly Cashback Info"
                  >
                    <Info size={20} />
                  </button>
                  <div
                    className={styles.bonusCard}
                    style={{
                      background:
                        'radial-gradient(116.81% 83.5% at 46.23% 9.2%, rgba(6, 115, 217, .6) 0%, rgba(6, 115, 217, 0) 60%)',
                    }}
                  >
                    <div className={styles.bonusIconWrapper}>
                      <img
                        src="/assets/images/bonus/bcc6.webp"
                        alt="Weekly Cashback"
                        className={styles.bonusIcon}
                        onError={(e) => {
                          e.target.src = '/assets/images/bonus/weekly.png';
                        }}
                      />
                    </div>
                    <div className={styles.bonusCardContent}>
                      <h3 className={styles.bonusCardTitle}>Weekly Cashback</h3>
                      <div className={styles.bonusCardStats}>
                        <span>
                          {bonuses?.bonuses?.weekly?.eligible
                            ? 'Bonus Unlocked'
                            : 'Available at VIP 25'}
                        </span>
                        {bonuses?.bonuses?.weekly?.eligible &&
                          bonuses?.bonuses?.weekly?.claimable && (
                            <span>${bonuses.bonuses.weekly.amount?.toFixed(2)}</span>
                          )}
                      </div>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => handleClaimBonus('weekly')}
                        disabled={
                          !bonuses?.bonuses?.weekly?.eligible ||
                          !bonuses?.bonuses?.weekly?.claimable ||
                          claimBonusMutation.isPending
                        }
                        className={styles.claimButton}
                      >
                        {claimBonusMutation.isPending ? 'Loading...' : 'Claim'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Monthly Cashback */}
                <div className={styles.bonusCardWrapper}>
                  <button
                    className={styles.infoIcon}
                    onClick={() => setSelectedInfo(BONUS_INFO.monthlyCashback)}
                    aria-label="Monthly Cashback Info"
                  >
                    <Info size={20} />
                  </button>
                  <div
                    className={styles.bonusCard}
                    style={{
                      background:
                        'radial-gradient(116.81% 83.5% at 46.23% 9.2%, rgba(179, 9, 61, .6) 0%, rgba(179, 9, 61, 0) 60%)',
                    }}
                  >
                    <div className={styles.bonusIconWrapper}>
                      <img
                        src="/assets/images/bonus/bcc7.webp"
                        alt="Monthly Cashback"
                        className={styles.bonusIcon}
                        onError={(e) => {
                          e.target.src = '/assets/images/bonus/monthly.png';
                        }}
                      />
                    </div>
                    <div className={styles.bonusCardContent}>
                      <h3 className={styles.bonusCardTitle}>Monthly Cashback</h3>
                      <div className={styles.bonusCardStats}>
                        <span>
                          {bonuses?.bonuses?.monthly?.eligible
                            ? 'Bonus Unlocked'
                            : 'Available at VIP 30'}
                        </span>
                        {bonuses?.bonuses?.monthly?.eligible &&
                          bonuses?.bonuses?.monthly?.claimable && (
                            <span>${bonuses.bonuses.monthly.amount?.toFixed(2)}</span>
                          )}
                      </div>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => handleClaimBonus('monthly')}
                        disabled={
                          !bonuses?.bonuses?.monthly?.eligible ||
                          !bonuses?.bonuses?.monthly?.claimable ||
                          claimBonusMutation.isPending
                        }
                        className={styles.claimButton}
                      >
                        {claimBonusMutation.isPending ? 'Loading...' : 'Claim'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BonusLoggedInView;
